import React, { useState, useEffect } from "react";
import Logo from "./job_description/Logo";
import { DayPicker } from "react-day-picker";
import { navigate } from "../../libraries/universal-route/router";
import { ClockIcon } from "@heroicons/react/24/solid";
import { joinWithCommasAnd } from "../../libraries/nodejs-manager/src/libraries/format/string";
import { candidateSelfBook as candidateSelfBookApi, get } from "../api";

const defaultTz = "America/New_York";

const buttonClasses =
  "text-indigo-600 rounded mt-4 first:mt-0 border-2 border-indigo-500 hover:border-indigo-700 hover:bg-indigo-50 p-2 text-center cursor-pointer";

const buttonActiveClasses =
  "text-white rounded mt-4 border-2 border-indigo-500 bg-indigo-500 hover:bg-indigo-700 p-2 text-center cursor-pointer";

const buttonGrayClasses =
  "text-white rounded mt-4 border-2 border-gray-500 bg-gray-500 hover:bg-gray-700 p-2 text-center cursor-pointer";

const formatAvailableTime = (time) => {
  const parts = time.split(":");
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  return `${hours > 12 ? hours - 12 : hours}:${
    minutes < 10 ? "0" : ""
  }${minutes} ${hours < 12 ? "AM" : "PM"}`;
};

const formatMinutes = (minutes) => {
  minutes = parseInt(minutes, 10);
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""}`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours} hour${hours > 1 ? "s" : ""} ${
    mins > 0 ? `minute${mins > 1 ? "s" : ""}` : ``
  }`;
};

const formateDateToDayString = (date) => {
  const year = date.getFullYear();
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const day = ("0" + date.getDate()).slice(-2);
  return `${year}-${month}-${day}`;
};

const Calendar = ({
  tomorrowIso,
  endDateIso,
  dateSelectedIso,
  baseUrl,
  blockedDates
}) => {
  const [mounted, setMounted] = useState(false);
  const [selected, setSelected] = useState();

  useEffect(() => {
    setMounted(true);
    setSelected(new Date(Date.parse(dateSelectedIso)));
    return () => {
      setSelected(null);
    };
  }, [dateSelectedIso]);

  if (!mounted) return null;

  return (
    <DayPicker
      mode="single"
      selected={selected}
      onSelect={(input) => {
        setSelected(input);
        navigate(`${baseUrl}/${formateDateToDayString(input)}`);
      }}
      fromDate={new Date(Date.parse(tomorrowIso))}
      toDate={new Date(Date.parse(endDateIso))}
      disabled={blockedDates}
    />
  );
};

const AvailableTime = ({
  startTime,
  endTime,
  setSelectedStartTime,
  isSelected,
  slug,
  jobId,
  candidateId,
  workflowIds,
  dateSelected,
  tz,
  dispatch
}) => {
  return (
    <>
      {!isSelected && (
        <div
          onClick={() => {
            setSelectedStartTime(startTime);
          }}
          className={buttonClasses}
        >
          <div className="text-lg">
            {formatAvailableTime(startTime)} - {formatAvailableTime(endTime)}
          </div>
        </div>
      )}
      {isSelected && (
        <div className="first:-mt-4 flex gap-x-2 flex-row items-center">
          <div
            onClick={() => {
              setSelectedStartTime(null);
            }}
            className={`${buttonGrayClasses} basis-1/2`}
          >
            {formatAvailableTime(startTime)}
          </div>
          <div
            onClick={async () => {
              await candidateSelfBookApi(slug)({
                startTime,
                jobId,
                candidateId,
                workflowIds,
                dateSelected,
                tz
              });
              const { data } = await get(window.location.href, {});
              dispatch({ type: "CHANGE_CONTENT", data });
            }}
            className={`${buttonActiveClasses} basis-1/2`}
          >
            Confirm
          </div>
        </div>
      )}
    </>
  );
};

const blockedDaysOfWeekToDates = (
  startDateIso,
  endDateIso,
  blockedDaysOfWeek
) => {
  let currentDate = new Date(startDateIso);
  const endDate = new Date(endDateIso);
  const blockedDates = [];

  while (currentDate <= endDate) {
    if (blockedDaysOfWeek.includes(currentDate.getDay()))
      blockedDates.push(new Date(currentDate));

    // increment the date by one day
    currentDate = new Date(currentDate.getTime() + 86400000);
  }

  return blockedDates;
};

const Availability = (props) => {
  const {
    dispatch,
    logo,
    orgName,
    colorHeaderText,
    colorHeaderBg,
    url,
    baseUrl,
    tz,
    availableTimes,
    availableDays,
    interviewers,
    job,
    workflows,
    candidate,
    durationInMins,
    round,
    tomorrowIso,
    endDateIso,
    dateSelectedIso,
    dateSelectedFormatted,
    slug,
    jobId,
    candidateId,
    workflowIds,
    eventBooked,
    bookedDate,
    bookedTime,
    errorMessage
  } = props;

  const unavailableDaysOfWeek = [0, 1, 2, 3, 4, 5, 6].reduce((acc, day) => {
    if (!availableDays.includes(day)) return acc.concat(day);
    return acc;
  }, []);

  const blockedDates = blockedDaysOfWeekToDates(
    tomorrowIso,
    endDateIso,
    unavailableDaysOfWeek
  );

  const { candidate_name: candidateName } = candidate;
  const { job_name: jobName } = job;

  const [selectedStartTime, setSelectedStartTime] = useState(null);

  // detect the client's timezone and redirect, or use default
  useEffect(() => {
    if (tz) return;

    const dateTimeFormat =
      Intl && "DateTimeFormat" in Intl
        ? Intl.DateTimeFormat()
        : { resolvedOptions: { timeZone: defaultTz } };
    const resolvedOptions =
      "resolvedOptions" in dateTimeFormat
        ? dateTimeFormat.resolvedOptions()
        : { timeZone: defaultTz };

    navigate(
      `${baseUrl}/${
        "timeZone" in resolvedOptions && resolvedOptions.length > 0
          ? encodeURIComponent(resolvedOptions["timeZone"])
          : encodeURIComponent(defaultTz)
      }`
    );
  }, [baseUrl, tz]);

  return (
    <div className="container mt-4 lg:px-20 lg:mt-20 mx-auto">
      <div className="border rounded-md overflow-hidden bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:divide-x">
          <div>
            <div
              className="pt-6 pl-4"
              style={{ backgroundColor: colorHeaderBg }}
            >
              <Logo
                logo={logo}
                name={orgName}
                colorHeaderText={colorHeaderText}
                url={url}
                small={true}
              />
              <div className="text-gray-400 font-semibold">{orgName}</div>
            </div>
            <div className="pl-4 pb-6">
              <div className="text-xl mt-6 font-semibold">
                {jobName}: {round}
                {round.includes(" Round") || round.includes(" Review")
                  ? ""
                  : " Round"}
              </div>

              <div className="text-base mt-4">👋 Hi, {candidateName}!</div>
              {interviewers.length > 0 && (
                <div className="text-base mt-4">
                  You will be meeting with{" "}
                  {joinWithCommasAnd(
                    interviewers.map(({ interviewer_name: name }) => name)
                  )}
                  .
                  {workflows.length > 1 &&
                    ` You will be meeting in ${workflows.length} back-to-back session.`}
                </div>
              )}
              <div className="text-base mt-4 flex gap-x-2 items-center">
                <ClockIcon className="h-5 w-5 text-gray-600" />
                <div>{formatMinutes(durationInMins)}</div>
              </div>
            </div>
          </div>
          <div className={`${eventBooked || errorMessage ? "col-span-2" : ""}`}>
            <div className="pt-0 md:pt-6 px-4 pb-6">
              <div className="text-3xl md:text-lg font-semibold">
                {!eventBooked
                  ? "Select a Date & Time"
                  : "Confirmed Date & Time"}
              </div>
              <div className="flex flex-col mt-12 md:mt-6 place-content-center md:place-content-start">
                {!eventBooked && !errorMessage && (
                  <div className="scale-125 md:scale-100">
                    <Calendar
                      tomorrowIso={tomorrowIso}
                      endDateIso={endDateIso}
                      dateSelectedIso={dateSelectedIso}
                      baseUrl={baseUrl}
                      blockedDates={blockedDates}
                    />
                  </div>
                )}
                {eventBooked && (
                  <div className="text-3xl mt-6 font-semibold">
                    {bookedDate}
                  </div>
                )}
                {eventBooked && (
                  <div className="text-3xl mt-2">{bookedTime}</div>
                )}
                {eventBooked && (
                  <div className="text-lg mt-6">
                    See your email and calendar for full details.
                  </div>
                )}
                {errorMessage && (
                  <div className="text-3xl mt-2">{errorMessage}</div>
                )}
              </div>
            </div>
          </div>
          {!eventBooked && !errorMessage && (
            <div>
              <div className="mt-6 md:mt-20 px-4 text-xl md:text-base">
                {dateSelectedFormatted}
              </div>
              <div className="mt-6 px-4 pb-6 lg:max-h-[24rem] lg:overflow-y-scroll">
                {availableTimes.map(({ startTime, endTime }, index) => (
                  <AvailableTime
                    key={index}
                    startTime={startTime}
                    endTime={endTime}
                    setSelectedStartTime={setSelectedStartTime}
                    isSelected={selectedStartTime === startTime}
                    slug={slug}
                    jobId={jobId}
                    candidateId={candidateId}
                    round={round}
                    workflowIds={workflowIds}
                    dateSelected={dateSelectedIso.split("T")[0]}
                    tz={tz}
                    dispatch={dispatch}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <style>
        {`
        body {
            background-color: #F4F4F5;
        }
        
        .rdp { margin: 0 !important; }

        .rdp-caption_label {
            font-size: 100% !important;
            font-weight: normal !important;
            padding-left: 0 !important;
        }

        .rdp-table {
            margin-left: -10px !important;
        }
        `}
      </style>
    </div>
  );
};

export default Availability;
